"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactBlocks = [{
  label: "GENERAL INQUIRIES",
  value: "hello@themissyvonne.com"
}, {
  label: "COMMERCIAL ACCOUNTS",
  value: "wholesale@themissyvonne.com"
}, {
  label: "SUPPORT",
  value: "care@themissyvonne.com"
}];
const inquiryOptions = ["Fresh Seafood Order", "Frozen Sampler Boxes", "Commercial Partnership", "General Question"];
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="5189" id="pricing-terms" className="dark bg-background text-foreground relative overflow-hidden" style={{
    paddingTop: "240px",
    paddingBottom: "200px"
  }}>
      <div ref={ref} className={`mx-auto max-w-[1440px] px-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-12 md:mb-16">
          <span className="font-mono uppercase text-xs tracking-[0.25em]" style={{
          color: "var(--primary)"
        }}>
            — CONTACT / REACH THE CREW
          </span>
        </div>

        <h2 className="font-default italic text-foreground mb-10" style={{
        fontSize: "clamp(4rem, 10vw, 12rem)",
        lineHeight: "0.95",
        letterSpacing: "-0.03em"
      }}>
          Reach <span className="text-muted-foreground">/</span> the{" "}
          <span className="text-muted-foreground">/</span> Crew
          <span className="inline-block align-baseline ml-2" style={{
          width: "0.5em",
          height: "0.5em",
          backgroundColor: "var(--primary)"
        }} />
        </h2>

        <p className="font-default italic text-muted-foreground mb-20 md:mb-24" style={{
        fontSize: "clamp(1.125rem, 1.5vw, 1.25rem)",
        maxWidth: "580px",
        lineHeight: "1.6"
      }}>
          Whether you have questions about our catch or need assistance with your delivery, our team is here to help.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-2 flex flex-col">
            <div className="space-y-8">
              {contactBlocks.map((block, i) => <div key={block.label} data-index={i} className="flex flex-col">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="block h-px w-6" style={{
                  backgroundColor: "var(--primary)"
                }} />
                    <span className="font-mono uppercase text-[0.75rem] tracking-[0.25em]" style={{
                  color: "var(--primary)"
                }}>
                      {block.label}
                    </span>
                  </div>
                  <span className="font-mono uppercase text-[0.75rem] text-muted-foreground tracking-wider pl-9">
                    {block.value}
                  </span>
                </div>)}
            </div>

            <div className="mt-12 pl-9">
              <p className="font-default italic text-muted-foreground text-base">
                — We respond to all inquiries within 24 hours, from shore to door.
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <FormProvider formKey="pricing_terms_form_commercial_accounts" formId="pricing_terms_form_commercial_accounts" sectionName="pricing-terms" defaultValues={{
            full_name: "",
            email: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2,
              maxLength: 100
            },
            email: {
              required: true,
              pattern: "email"
            },
            inquiry_type: {
              required: false
            },
            message: {
              required: false
            }
          }} submitText="Send Message" successMessage="Your message has been received. We will be in touch shortly." errorMessage="We could not transmit your message. Please try again." className="relative" style={{
            backgroundColor: "hsl(222 47% 12% / 0.4)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            border: "1px solid var(--border)",
            padding: "56px",
            boxShadow: "0 10px 30px -15px rgba(0,0,0,0.1)"
          }}>
              <ContactForm />
            </FormProvider>

            <div className="mt-8 pt-6 border-t" style={{
            borderColor: "var(--border)"
          }}>
              <p className="font-mono text-[0.75rem] uppercase tracking-[0.15em] text-muted-foreground">
                — ALL CORRESPONDENCE IS HANDLED WITH CARE.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-8">
        <p className="font-mono uppercase text-[0.75rem] tracking-[0.25em] mb-6" style={{
        color: "var(--primary)"
      }}>
          — MESSAGE RECEIVED
        </p>
        <p className="font-default italic text-foreground text-2xl leading-snug mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono uppercase text-[0.75rem] tracking-[0.25em] h-12 px-6 bg-transparent border-border text-foreground hover:bg-accent">
          Send Another Message
        </Button>
      </div>;
  }
  const minimalFieldClass = "bg-transparent border-0 border-b border-border rounded-none px-0 py-3 h-auto text-foreground font-default text-base placeholder:text-muted-foreground placeholder:italic focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-[var(--primary)] shadow-none transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono uppercase text-[0.75rem] tracking-[0.25em]" style={{
        color: "var(--primary)"
      }}>
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Captain Name" className={minimalFieldClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono uppercase text-[0.75rem] tracking-[0.25em]" style={{
        color: "var(--primary)"
      }}>
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="captain@example.com" className={minimalFieldClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono uppercase text-[0.75rem] tracking-[0.25em]" style={{
        color: "var(--primary)"
      }}>
              Inquiry Type
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="bg-transparent border-0 border-b border-border px-0 py-3 h-auto w-full text-foreground font-default text-base focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-[var(--primary)] data-[placeholder]:italic data-[placeholder]:text-muted-foreground">
                  <SelectValue placeholder="Select an inquiry type" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {inquiryOptions.map((option, i) => <SelectItem key={option} data-index={i} value={option} className="font-default">
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono uppercase text-[0.75rem] tracking-[0.25em]" style={{
        color: "var(--primary)"
      }}>
              Your Message
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="How can we help you today?" className="bg-transparent border-0 border-b border-border px-0 py-3 text-foreground font-default text-base placeholder:text-muted-foreground placeholder:italic focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-[var(--primary)] resize-none transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border" style={{
      backgroundColor: "hsl(0 84% 60% / 0.1)",
      borderColor: "var(--destructive)"
    }}>
          <p className="font-mono uppercase text-[0.75rem] tracking-wider text-destructive">
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 bg-secondary text-white hover:bg-primary font-mono uppercase text-[0.75rem] tracking-[0.25em]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : <>
            <ArrowRight className="mr-2 size-4" />
            Send Message
          </>}
      </Button>
    </Form>;
}